import { customFormat, parseCalendarDate } from 'utils/dateUtils'

import { toCalendarIso } from '../collectionPeriodBounds'

const ACTIVITY_FORMAT = 'd MMM yyyy, HH:mm'
const DAY_FORMAT = 'd MMM yyyy'
const NO_DEPARTMENT = 'No department'
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T/
export const PARTICIPANTS_PAGE_SIZE = 25
const MANAGER_ESCALATION_MANUAL = 2

export const STATUS_FROM_API = {
  not_started: 'notStarted',
  in_progress: 'started',
  completed: 'trained',
  overdue: 'overdue',
}

export const STATUS_TO_API = {
  notStarted: 'not_started',
  started: 'in_progress',
  trained: 'completed',
  overdue: 'overdue',
}

export const ATTR_TO_API = {
  delivered: 'delivered',
  notDelivered: 'not_delivered',
  pending: 'pending',
  extended: 'extended_deadline',
  paused: 'reminders_paused',
  manual: 'manual_completion',
}

export const TIMELINESS_FROM_API = {
  on_time: 'onTime',
  late: 'late',
}

export const TIMELINESS_TO_API = {
  onTime: 'on_time',
  late: 'late',
}

const ORDER_TO_API = {
  name: 'name',
  department: 'department',
  status: 'status',
  lastActivity: 'last_activity',
  reminders: 'reminders',
  managerReminder: 'manager_escalation',
}

const DELIVERY_FROM_API = {
  not_delivered: 'notDelivered',
  pending: 'pending',
}

export const EMPTY_CAMPAIGN_PARTICIPANTS = {
  available: false,
  assigned: null,
  matchingCount: null,
  participants: [],
  departments: [],
}

export const formatParticipantDay = (isoDate) => {
  if (isoDate == null || isoDate === '') {
    return null
  }
  const day = DATE_TIME.test(String(isoDate)) ? String(isoDate).slice(0, 10) : isoDate
  const date = parseCalendarDate(day)
  if (!date) {
    return null
  }
  return customFormat(date, DAY_FORMAT, false)
}

export const formatParticipantActivity = (isoDate) => {
  if (isoDate == null || isoDate === '') {
    return null
  }
  if (DATE_TIME.test(String(isoDate))) {
    const date = new Date(isoDate)
    if (Number.isNaN(date.getTime())) {
      return null
    }
    return customFormat(date, ACTIVITY_FORMAT, false)
  }
  const date = parseCalendarDate(isoDate)
  if (!date) {
    return null
  }
  return customFormat(date, ACTIVITY_FORMAT, false)
}

const deptName = (value) => {
  if (value == null || value === '') {
    return NO_DEPARTMENT
  }
  return value
}

const mapList = (values, mapping) =>
  (values || []).map((value) => mapping[value] || value).filter(Boolean)

const adaptReminderItems = (items, isManual) =>
  (items || []).map((item) => ({
    date: formatParticipantDay(item.sentAt || item.sent_at) || item.sentAt || item.sent_at,
    manual: isManual(item),
  }))

const adaptReminder = (reminder, isManual) => {
  const lastSentAt = reminder?.lastSentAt || reminder?.last_sent_at || null
  if (!reminder || (!reminder.count && !lastSentAt)) {
    return null
  }
  const items = adaptReminderItems(reminder.items, isManual)
  const manualItems = items.filter((item) => item.manual)
  return {
    count: reminder.count,
    date: formatParticipantDay(lastSentAt),
    lastSentAt,
    items,
    manual: manualItems.length,
    manualDates: manualItems.map((item) => item.date),
  }
}

const adaptAttributes = (row, escalation) => {
  const attrs = {}
  const delivery = DELIVERY_FROM_API[row.deliveryHealth]
  if (delivery) {
    attrs.delivery = delivery
  }
  if (row.extendedDeadline) {
    attrs.extendedTo = formatParticipantDay(row.extendedDeadline) || row.extendedDeadline
  }
  if (row.remindersPausedUntil) {
    attrs.pausedUntil = formatParticipantDay(row.remindersPausedUntil) || row.remindersPausedUntil
  }
  if (row.completionMethod === 'manual') {
    attrs.method = 'manual'
  }
  const manual = row.manualCompletion
  if (manual) {
    attrs.method = 'manual'
    if (manual.markedBy) {
      attrs.manualBy = manual.markedBy
    }
    if (manual.markedOn) {
      attrs.manualDate = formatParticipantDay(manual.markedOn) || manual.markedOn
    }
    if (manual.reason) {
      attrs.manualReason = manual.reason
    }
  }
  const timeliness = TIMELINESS_FROM_API[row.timeliness]
  if (timeliness) {
    attrs.timeliness = timeliness
  }
  if (row.remindedToday) {
    attrs.remindedToday = true
  }
  if (escalation?.managerNotifiedToday) {
    attrs.managerNotifiedToday = true
  }
  if (!escalation?.hasManager) {
    attrs.noManager = true
  }
  return Object.keys(attrs).length ? attrs : null
}

const adaptRow = (row) => {
  const escalation = row.managerEscalation
  const hasManager = Boolean(escalation?.hasManager)
  const attrs = adaptAttributes(row, escalation)
  return {
    id: row.id,
    profileId: row.profileId,
    name: row.fullName,
    department: deptName(row.department),
    status: STATUS_FROM_API[row.status] || 'notStarted',
    lastActivity: formatParticipantActivity(row.lastActivityAt),
    reminders: adaptReminder(row.reminders, (item) => item.isAuto === false),
    managerReminder: adaptReminder(escalation, (item) => item.source === MANAGER_ESCALATION_MANUAL),
    managerLastSentAt: escalation?.lastSentAt || escalation?.last_sent_at || null,
    lastAssignmentResendAt: row.lastAssignmentResendAt || row.last_assignment_resend_at || null,
    managerName: (escalation?.managerName || '').trim() || (hasManager ? 'Manager' : null),
    attrs,
    extendedDeadlineIso: toCalendarIso(row.extendedDeadline),
    effectiveDeadlineIso: toCalendarIso(row.effectiveDeadline),
  }
}

export const adaptCampaignParticipants = (payload) => {
  if (payload == null || payload.available === false) {
    return EMPTY_CAMPAIGN_PARTICIPANTS
  }

  const meta = payload.meta || {}
  const participants = (payload.results || []).map(adaptRow)
  const departments = (meta.departments || []).map(deptName)
  const uniqueDepartments = [
    ...new Set(departments.length ? departments : participants.map((p) => p.department)),
  ]

  return {
    available: true,
    assigned: meta.assignedCount ?? participants.length,
    matchingCount: meta.matchingCount ?? participants.length,
    participants,
    departments: uniqueDepartments,
  }
}

export const toParticipantsCsvBody = (query = {}) => {
  const params = {}
  const search = (query.search || '').trim()
  if (search.length >= 2) {
    params.search = search
  }
  const departments = query.departments || []
  if (departments.length) {
    params.departments = departments
  }
  const statuses = mapList(query.statuses, STATUS_TO_API)
  if (statuses.length) {
    params.statuses = statuses
  }
  const attributes = mapList(query.attributes, ATTR_TO_API)
  if (attributes.length) {
    params.attributes = attributes
  }
  const timeliness = mapList(query.timeliness, TIMELINESS_TO_API)
  if (timeliness.length) {
    params.timeliness = timeliness
  }
  return params
}

export const toTrainingParticipantsQuery = (query = {}) => {
  const params = toParticipantsCsvBody(query)
  const sort = query.sort
  if (sort?.key && sort.direction) {
    const field = ORDER_TO_API[sort.key]
    if (field) {
      params.ordering = sort.direction === 'desc' ? `-${field}` : field
    }
  } else if (query.ordering && query.ordering !== 'risk') {
    params.ordering = query.ordering
  }
  const page = query.page || 1
  if (page > 1) {
    params.page = page
  }
  params.page_size = query.pageSize || PARTICIPANTS_PAGE_SIZE
  return params
}
